import Base from '../base/base';
import {
  ConsentRecord,
  readConsent,
  writeConsent,
  hasGPC,
  gpcNoticeSeen,
  markGpcNoticeSeen
} from '../../shared/utils/consent';
import { ConsentCategory, DEFAULT_CATEGORIES } from './consent-categories';
import {
  COPY,
  bannerHtml,
  noticeHtml,
  panelHtml,
  rowHtml
} from './cookie-consent.html';
import './cookie-consent.scss';

const DEFAULT_PRIVACY_URL = '/legal#privacy';
const DEFAULT_TRIGGER_SELECTOR = '[data-cookie-consent-trigger]';

const BANNER_CLASS = 'kl-consent';
const NOTICE_CLASS = 'kl-consent--notice';
const PANEL_CLASS = 'kl-consent-panel';

const CONSENT_ATTRIBUTE = 'data-consent';
const CATEGORY_ATTRIBUTE = 'data-category';
const PANEL_ATTRIBUTE = 'data-panel';

const CONSENT_SELECTOR = `[${CONSENT_ATTRIBUTE}]`;
const CATEGORY_SELECTOR = `[${CATEGORY_ATTRIBUTE}]`;
const PANEL_CLOSE_SELECTOR = `[${PANEL_ATTRIBUTE}="close"]`;

/** Today the site ships opt-out. Flip both to return to opt-in. */
const DEFAULT_GRANTED = true;
const DEFAULT_SHOW_BANNER = false;

const CHOICE = {
  accept: 'accept',
  reject: 'reject',
  manage: 'manage',
  dismiss: 'dismiss',
  save: 'save'
};

interface CookieConsentOptions {
  categories?: ConsentCategory[];
  /** Where the policy link points */
  privacyUrl?: string;
  /** Any element carrying this opens the preferences panel */
  triggerSelector?: string;
  /** What a visitor who has not chosen gets, before any signal */
  defaultGranted?: boolean;
  /** Whether a visitor who has not chosen is asked on a first visit */
  showBanner?: boolean;
  message?: string;
  debug?: boolean;
  [key: string]: any;
}

export default class CookieConsent extends Base {
  private categories: ConsentCategory[];
  private privacyUrl: string;
  private triggerSelector: string;
  private defaultGranted: boolean;
  private showBanner: boolean;
  private message: string;

  private banner: HTMLElement | null = null;
  private panel: HTMLElement | null = null;
  private draft: ConsentRecord = {};

  private onDocumentClick: ((event: MouseEvent) => void) | null = null;

  constructor(options: CookieConsentOptions = {}) {
    super(options.debug || false);
    this.categories = options.categories || DEFAULT_CATEGORIES;
    this.privacyUrl = options.privacyUrl || DEFAULT_PRIVACY_URL;
    this.triggerSelector =
      options.triggerSelector || DEFAULT_TRIGGER_SELECTOR;
    this.defaultGranted =
      options.defaultGranted === undefined ? DEFAULT_GRANTED : options.defaultGranted;
    this.showBanner =
      options.showBanner === undefined ? DEFAULT_SHOW_BANNER : options.showBanner;
    this.message = options.message || COPY.banner.message;

    this.init();
  }

  init(): void {
    this.bindTriggers();

    const stored = readConsent();

    if (stored) {
      this.apply(stored);
      this.log('stored choice applied', stored);
      return;
    }

    // GPC stands in for a visitor who has not chosen yet.
    if (hasGPC()) {
      this.apply(this.allSetTo(false));
      this.log('GPC present, non-essential categories held off');
      if (!gpcNoticeSeen()) this.renderGpcNotice();
      return;
    }

    this.apply(this.allSetTo(this.defaultGranted));
    if (this.showBanner) this.renderBanner();
  }

  /** Delegated, so a trigger added after load still opens the panel. */
  private bindTriggers(): void {
    this.onDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !target.closest) return;

      if (target.closest(this.triggerSelector)) {
        event.preventDefault();
        this.openPanel();
      }
    };

    document.addEventListener('click', this.onDocumentClick as EventListener);
  }

  private allSetTo(value: boolean): ConsentRecord {
    return this.categories.reduce((record, category) => {
      record[category.id] = category.locked ? true : value;
      return record;
    }, {} as ConsentRecord);
  }

  /** Runs each category's hook once, for the state the record names. */
  private apply(record: ConsentRecord): void {
    this.categories.forEach((category) => {
      const on = category.locked ? true : record[category.id] === true;
      if (on) category.onEnable();
      else category.onDisable();
    });
  }

  private resolve(record: ConsentRecord): void {
    writeConsent(record);
    this.apply(record);
    this.closeBanner();
    this.closePanel();
    this.log('choice saved', record);
  }

  private renderBanner(): void {
    const banner = this.createShell(COPY.banner.ariaLabel);
    banner.innerHTML = bannerHtml(this.privacyUrl, this.message);

    document.body.appendChild(banner);
    this.banner = banner;
    this.log('banner shown');
  }

  /** Tells a GPC visitor we saw the signal, since they never see the banner. */
  private renderGpcNotice(): void {
    const notice = this.createShell(COPY.notice.ariaLabel);
    notice.classList.add(NOTICE_CLASS);
    notice.innerHTML = noticeHtml(this.privacyUrl);

    document.body.appendChild(notice);
    this.banner = notice;
    this.log('GPC notice shown');
  }

  /** One shell, one click handler, for the banner and the GPC notice alike. */
  private createShell(label: string): HTMLElement {
    const shell = document.createElement('div');
    shell.className = BANNER_CLASS;
    shell.setAttribute('role', 'dialog');
    shell.setAttribute('aria-live', 'polite');
    shell.setAttribute('aria-label', label);

    shell.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest(CONSENT_SELECTOR);
      if (!button) return;

      const choice = button.getAttribute(CONSENT_ATTRIBUTE);
      if (choice === CHOICE.accept) this.resolve(this.allSetTo(true));
      if (choice === CHOICE.reject) this.resolve(this.allSetTo(false));
      if (choice === CHOICE.manage) this.openPanel();
      if (choice === CHOICE.dismiss) {
        markGpcNoticeSeen();
        this.closeBanner();
        this.log('GPC notice dismissed');
      }
    });

    return shell;
  }

  openPanel(): void {
    if (this.panel) return;

    const stored = readConsent();
    this.draft = stored
      ? { ...this.allSetTo(this.defaultGranted), ...stored }
      : this.allSetTo(this.defaultGranted);

    const panel = document.createElement('div');
    panel.className = PANEL_CLASS;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', COPY.panel.ariaLabel);
    panel.innerHTML = panelHtml(this.privacyUrl, this.renderRows());

    panel.addEventListener('change', (event) => {
      const input = event.target as HTMLInputElement;
      if (!input.matches(CATEGORY_SELECTOR)) return;
      this.draft[input.getAttribute(CATEGORY_ATTRIBUTE) as string] = input.checked;
    });

    panel.addEventListener('click', (event) => {
      const element = event.target as HTMLElement;

      if (element.closest(PANEL_CLOSE_SELECTOR)) {
        this.closePanel();
        return;
      }

      const button = element.closest(CONSENT_SELECTOR);
      if (!button) return;

      const choice = button.getAttribute(CONSENT_ATTRIBUTE);
      if (choice === CHOICE.accept) this.resolve(this.allSetTo(true));
      if (choice === CHOICE.reject) this.resolve(this.allSetTo(false));
      if (choice === CHOICE.save) this.resolve({ ...this.draft });
    });

    document.body.appendChild(panel);
    this.panel = panel;
    this.log('panel opened', this.draft);
  }

  private renderRows(): string {
    return this.categories
      .map((category) =>
        rowHtml(
          category,
          category.locked ? true : this.draft[category.id] === true
        )
      )
      .join('');
  }

  private closeBanner(): void {
    this.banner?.remove();
    this.banner = null;
  }

  closePanel(): void {
    this.panel?.remove();
    this.panel = null;
  }

  destroy(): void {
    if (this.onDocumentClick) {
      document.removeEventListener(
        'click',
        this.onDocumentClick as EventListener
      );
      this.onDocumentClick = null;
    }
    this.closeBanner();
    this.closePanel();
  }
}
